import React, { useState } from "react";
import {
  Box,
  Card,
  Chip,
  Divider,
  Grid,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from "@mui/material";

/**
 * Project Documentation — an in-app reference for Step Up Academy.
 * Self-contained (no markdown dependency); rendered from a section model
 * with a sticky table-of-contents. Reachable from the sidebar (HELP → Documentation).
 */

type Section = { id: string; label: string; body: React.ReactNode };

const SectionTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography variant="h5" sx={{ fontWeight: 700, mb: 1.5 }}>
    {children}
  </Typography>
);

const SubTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography variant="subtitle1" sx={{ fontWeight: 700, mt: 2.5, mb: 1 }}>
    {children}
  </Typography>
);

const P: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography variant="body2" sx={{ mb: 1.25, lineHeight: 1.7, color: "text.secondary" }}>
    {children}
  </Typography>
);

const Bullets: React.FC<{ items: React.ReactNode[] }> = ({ items }) => (
  <Box component="ul" sx={{ pl: 3, mb: 1.5 }}>
    {items.map((it, i) => (
      <Typography key={i} component="li" variant="body2" sx={{ mb: 0.5, lineHeight: 1.6, color: "text.secondary" }}>
        {it}
      </Typography>
    ))}
  </Box>
);

const Code: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box
    component="code"
    sx={{
      px: 0.75,
      py: 0.2,
      borderRadius: 1,
      bgcolor: "action.hover",
      fontFamily: "monospace",
      fontSize: "0.85em",
    }}
  >
    {children}
  </Box>
);

const SimpleTable: React.FC<{ head: string[]; rows: React.ReactNode[][] }> = ({ head, rows }) => (
  <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
    <Table size="small">
      <TableHead>
        <TableRow>
          {head.map((h) => (
            <TableCell key={h} sx={{ fontWeight: 700 }}>
              {h}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((r, i) => (
          <TableRow key={i}>
            {r.map((c, j) => (
              <TableCell key={j} sx={{ verticalAlign: "top" }}>
                {c}
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
);

const MODULES: [string, string, string][] = [
  ["Dashboard", "Dashboard_View", "Landing metrics and summaries"],
  ["Students", "Student_View/Create/Edit/Delete", "Student records, parents, grade assignment"],
  ["Staff", "Staff_View/Create/Edit/Delete", "Staff (team) records, departments, positions"],
  ["Classes", "Classes_View/Create/Edit/Delete", "Class assignment, enrollment, calendar"],
  ["Class Schedule", "ClassSchedule_*", "Concrete session occurrences, conflicts, cancellations"],
  ["Attendance", "AttendanceRecord_*", "Mark/report student & class attendance"],
  ["Session Data", "SessionData_*", "ABA tutoring session records & progress"],
  ["Programs / Library", "Program_*, Program_Library_*, ProgramTarget_*", "Program library, targets, prompt templates, mastery"],
  ["Courses (LMS)", "Course_*, Assignment_*", "Courses, materials, assignments, submissions"],
  ["Invoices", "Invoice_*", "Invoice creation, items, sending, overdue"],
  ["Payments", "Payment_*", "Payments, methods, manual entry, history"],
  ["Finance / Ledger", "Ledger_*, Expense_*, Receipt_*, Report_*", "Accounts, expenses, transactions, reports"],
  ["Payroll", "Payroll_*, PunchDetails_*, EmployeeSchedule_*", "Punch records, schedules, payroll generation"],
  ["Email / SMS", "Email_*, Template_*, Sms_*", "Records, templates, sending/resending"],
  ["Messages / Chat", "Message_*", "Internal messaging (notifications & chat open to all)"],
  ["Users & Roles", "User_*, User_Role_*, Privilege_*, Permission_View", "User accounts, roles, the permission matrix"],
  ["Settings", "Setting_*", "Company, theme, locations, grades/subjects, email config"],
  ["Activity Log", "ActivityLog_View", "Audit trail of create/edit/delete actions"],
];

const ROLES: [string, string, string][] = [
  ["Super Admin", "199", "Full access; holds System_Admin → bypasses every check"],
  ["Staff – Manager (Sub-Admin)", "196", "Everything except deleting settings/users and System_Admin"],
  ["Staff – Front Desk (Receptionist)", "22", "Students (create/edit), classes (view), messages"],
  ["Staff – Finance (Accountant)", "19", "Invoices, payments, receipts, expenses, reports + student read"],
  ["Staff – Tutor", "19", "Students (view), classes (view), session data, attendance"],
];

const SECTIONS: Section[] = [
  {
    id: "overview",
    label: "Overview",
    body: (
      <>
        <SectionTitle>Overview</SectionTitle>
        <P>
          This application is a school / tutoring / therapy-centre management system
          covering students, staff, classes &amp; scheduling, attendance, ABA-style session
          data, billing (invoices, payments, finance/ledger), payroll, and communications
          — all gated by a role-based permission matrix. Company identity (name, logo,
          address, contact, colors, socials) is driven entirely by <em>Settings → Company</em>;
          the same codebase deploys as any organisation without code edits.
        </P>
        <SubTitle>Technology stack</SubTitle>
        <Bullets
          items={[
            <>Backend: <strong>Laravel 11</strong> (PHP), MySQL database <Code>stepup_admin_db</Code></>,
            <>Frontend: <strong>React 18 + TypeScript + Vite</strong>, Material UI, Redux Toolkit (RTK Query)</>,
            <>Auth: <strong>Laravel Sanctum</strong> bearer tokens</>,
            <>State/data: RTK Query API layer (<Code>attoDeskApi.ts</Code>) with a <Code>useUser</Code> hook for permission checks</>,
          ]}
        />
      </>
    ),
  },
  {
    id: "architecture",
    label: "Architecture",
    body: (
      <>
        <SectionTitle>Architecture</SectionTitle>
        <P>
          The SPA talks to the Laravel API under <Code>/api</Code>. Every request after login carries a
          Sanctum bearer token. Protected routes sit inside an <Code>auth:sanctum</Code> group; most also
          carry a <Code>permission:</Code> middleware that enforces the role-based matrix.
        </P>
        <SubTitle>Request flow</SubTitle>
        <Bullets
          items={[
            <>User logs in → API returns a token + the resolved <Code>privileges</Code> array.</>,
            <>SPA stores the token and uses <Code>privileges</Code> to show/hide menus &amp; buttons.</>,
            <>Each API call passes <Code>Authorization: Bearer &lt;token&gt;</Code>.</>,
            <><Code>CheckPermission</Code> middleware returns <strong>403</strong> if the role lacks the required permission (frontend hiding is not security).</>,
          ]}
        />
        <SubTitle>API response shape</SubTitle>
        <P>
          Endpoints return <Code>{`{ code, status, message, data }`}</Code>. Errors use the matching HTTP
          status (401 unauthenticated, 403 forbidden, 422 validation).
        </P>
      </>
    ),
  },
  {
    id: "auth",
    label: "Authentication",
    body: (
      <>
        <SectionTitle>Authentication</SectionTitle>
        <SubTitle>Login</SubTitle>
        <P>
          <Code>POST /api/login</Code> with <Code>username</Code> + <Code>password</Code>. The account must
          have <Code>isDelete = 0</Code> and <Code>status = active</Code>. On success it returns a Sanctum
          token and the privilege list that drives the UI.
        </P>
        <SubTitle>Forgot password (OTP)</SubTitle>
        <Bullets
          items={[
            <>Find account → choose a masked delivery method (email or SMS).</>,
            <>Email sends a reset link; SMS sends a 6-digit OTP code (hashed, 10-min expiry, attempt-limited).</>,
            <>Verify code → set a new password.</>,
          ]}
        />
        <SubTitle>Self-service</SubTitle>
        <P>
          <Code>/user/system/change-password</Code> and <Code>/user/userInfo</Code> stay open to any
          authenticated user. Admin-initiated resets use <Code>/password/send-reset-link</Code> (User_Edit).
        </P>
      </>
    ),
  },
  {
    id: "roles",
    label: "Roles & Permissions",
    body: (
      <>
        <SectionTitle>Roles &amp; Permissions</SectionTitle>
        <P>
          Permissions are <strong>name-based</strong> using the <Code>{`{Module}_{Action}`}</Code> convention
          (e.g. <Code>Student_View</Code>, <Code>Invoice_Create</Code>). Actions are{" "}
          <strong>View / Create / Edit / Delete</strong>. A role is a row in <Code>user_roles</Code>; its
          grants live in <Code>role_permissions</Code>. A user points at one role via{" "}
          <Code>users.user_role</Code>.
        </P>
        <SubTitle>Default roles</SubTitle>
        <SimpleTable
          head={["Role", "Perms", "Scope"]}
          rows={ROLES.map((r) => [<strong>{r[0]}</strong>, <Chip size="small" label={r[1]} />, r[2]])}
        />
        <SubTitle>Enforcement (two layers)</SubTitle>
        <Bullets
          items={[
            <><strong>Backend:</strong> <Code>permission:</Code> route middleware → real 403. Reads → <Code>_View</Code>, create/update → <Code>_Create|_Edit</Code>, delete → <Code>_Delete</Code>. Holders of <Code>System_Admin</Code> bypass all checks.</>,
            <><strong>Frontend:</strong> <Code>useUser().can(prefix, action)</Code> and <Code>userHasRight(name)</Code> hide menus &amp; action buttons. This is convenience, not security.</>,
          ]}
        />
        <P>
          Note: shared lookup endpoints are gated by the consuming screen's permission. For example the
          Accountant has <Code>Student_View</Code> so the invoice/payment student dropdown works, and Front
          Desk/Tutor keep <Code>Classes_View</Code> for the class calendar lookups.
        </P>
      </>
    ),
  },
  {
    id: "matrix",
    label: "Permission Matrix",
    body: (
      <>
        <SectionTitle>Permission Matrix</SectionTitle>
        <P>
          The matrix UI (Users &amp; Roles → Permission Matrix) is how access is managed — no code change
          required. Pick a role, toggle module × action checkboxes, and save.
        </P>
        <Bullets
          items={[
            "Each row is a module; columns are View / Create / Edit / Delete.",
            "Special flags (System_Admin, Generate_Payroll, Punch_Admin, email_view) appear separately.",
            "Saving writes role_permissions; the change takes effect on the role's next login.",
            "System_Admin, Setting_Delete and User_Delete are intentionally admin-only.",
          ]}
        />
        <P>
          To verify the chain end-to-end: toggle a permission, save, re-login as that role, and confirm both
          the UI and a direct API call change accordingly.
        </P>
      </>
    ),
  },
  {
    id: "modules",
    label: "Modules",
    body: (
      <>
        <SectionTitle>Modules</SectionTitle>
        <P>Each module maps to a permission prefix. The matrix controls who can do what.</P>
        <SimpleTable
          head={["Module", "Permission prefix", "What it does"]}
          rows={MODULES.map((m) => [<strong>{m[0]}</strong>, <Code>{m[1]}</Code>, m[2]])}
        />
      </>
    ),
  },
  {
    id: "data",
    label: "Data Model",
    body: (
      <>
        <SectionTitle>Data model highlights</SectionTitle>
        <Bullets
          items={[
            <><Code>users</Code> — login accounts; <Code>user_role</Code> → <Code>user_roles.id</Code>.</>,
            <><Code>user_roles</Code> — roles (the role table is named user_roles, not roles).</>,
            <><Code>permissions</Code> — every <Code>{`{Module}_{Action}`}</Code> name + special flags.</>,
            <><Code>role_permissions</Code> — which permissions a role holds.</>,
            <><Code>students</Code> / <Code>student_parents</Code> — students linked to a parent &amp; grade.</>,
            <><Code>teams</Code> — staff records (department, position, salary).</>,
            <><Code>invoices</Code> / <Code>invoice_items</Code> / <Code>payments</Code> — billing.</>,
            <><Code>assign_classes</Code> + <Code>assign_classes_schedules</Code> + <Code>class_schedules</Code> — class assignment and concrete sessions.</>,
            <><Code>subjects_grades_tables</Code> — subject↔grade combos linked to staff &amp; students.</>,
          ]}
        />
      </>
    ),
  },
  {
    id: "testing",
    label: "Dev & Testing",
    body: (
      <>
        <SectionTitle>Development &amp; testing</SectionTitle>
        <SubTitle>Test accounts</SubTitle>
        <P>
          Five role logins exist for QA (password <Code>Test@1234</Code>):{" "}
          <Code>testadmin</Code>, <Code>testmanager</Code>, <Code>testfrontdesk</Code>,{" "}
          <Code>testaccountant</Code>, <Code>testtutor</Code> @stepup.test.
        </P>
        <SubTitle>Seeded sample data</SubTitle>
        <Bullets
          items={[
            "3 students (under one parent), 2 staff, grades G1/G2, subjects Math/English",
            "2 invoices (one partial, one paid) + matching payments",
            "1 class (Grade 1 Math) with Mon/Wed schedule, 2 enrolled students, 6 upcoming sessions",
          ]}
        />
        <SubTitle>Verifying enforcement</SubTitle>
        <P>
          For any "should be blocked" case, confirm both that the button/menu is hidden and that a direct
          API call returns <strong>403</strong>. See the project QA checklist (<Code>QA_TEST_CHECKLIST.md</Code>)
          for the full module-by-role grid. A DB backup is kept under{" "}
          <Code>backend/storage/db-backups/</Code> for resets.
        </P>
      </>
    ),
  },
];

const Documentation: React.FC = () => {
  const theme = useTheme();
  const [active, setActive] = useState<string>(SECTIONS[0].id);

  const scrollTo = (id: string) => {
    setActive(id);
    const el = document.getElementById(`doc-${id}`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <Box sx={{ pb: 4 }}>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h4" sx={{ fontWeight: 800 }}>
          Documentation
        </Typography>
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          Project reference — architecture, roles &amp; permissions, modules, and testing.
        </Typography>
      </Box>

      <Grid container spacing={2}>
        {/* Table of contents */}
        <Grid item xs={12} md={3}>
          <Card
            sx={{
              p: 1,
              position: { md: "sticky" },
              top: { md: 80 },
            }}
          >
            <Typography
              sx={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", color: "text.disabled", px: 1.5, py: 1 }}
            >
              CONTENTS
            </Typography>
            <List dense disablePadding>
              {SECTIONS.map((s) => (
                <ListItemButton
                  key={s.id}
                  selected={active === s.id}
                  onClick={() => scrollTo(s.id)}
                  sx={{ borderRadius: 1.5, mb: 0.25 }}
                >
                  <ListItemText
                    primary={s.label}
                    primaryTypographyProps={{
                      fontSize: 13.5,
                      fontWeight: active === s.id ? 700 : 500,
                      color: active === s.id ? theme.palette.primary.main : "text.primary",
                    }}
                  />
                </ListItemButton>
              ))}
            </List>
          </Card>
        </Grid>

        {/* Content */}
        <Grid item xs={12} md={9}>
          {SECTIONS.map((s, i) => (
            <Card key={s.id} id={`doc-${s.id}`} sx={{ p: { xs: 2, md: 3 }, mb: 2, scrollMarginTop: 88 }}>
              {s.body}
              {i < SECTIONS.length - 1 && <Divider sx={{ mt: 2, display: { md: "none" } }} />}
            </Card>
          ))}
        </Grid>
      </Grid>
    </Box>
  );
};

export default Documentation;
